import { useContext, useEffect, useState } from "react";
import { getFirstAccessibleRoute } from "../../utils/getFirstAccessibleRoute";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import {
    EyeInvisibleOutlined, EyeOutlined, UserOutlined, MailOutlined, PhoneOutlined, LockOutlined, BankOutlined,
    ThunderboltOutlined, TeamOutlined, LineChartOutlined, BellOutlined, ArrowRightOutlined, CheckCircleFilled,
} from "@ant-design/icons";
import { CREATE_LOGIN_USER_URL, LOGIN_URL } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { Checkbox } from "antd";

// ------------------- COMPONENTS -------------------

const LogoMark = ({ className = "w-10 h-10" }) => (
    <div className={`${className} rounded-xl bg-gradient-to-br from-[var(--brand-from)] to-[var(--brand-to)] flex items-center justify-center shadow-[0_8px_20px_-8px_var(--brand-glow)]`}>
        <svg className="w-1/2 h-1/2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M3 17l6-6 4 4 8-8M15 7h6v6" />
        </svg>
    </div>
);

const inputClass = (hasError, padRight) =>
    `w-full h-11 pl-10 ${padRight ? "pr-11" : "pr-4"} text-sm rounded-lg border bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 outline-none transition-colors duration-150 ` +
    (hasError
        ? "border-red-400 focus:ring-4 focus:ring-red-100 dark:focus:ring-red-900/40"
        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-brand-500 focus:ring-4 focus:ring-brand-100 dark:focus:ring-brand-900/50");

const FieldError = ({ id, message }) =>
    message ? <p id={id} className="text-red-500 text-xs mt-1.5">{message}</p> : null;

const PasswordInput = ({ value, onChange, error, placeholder = "Enter your password", autoComplete }) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
        <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Password
            </label>
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <LockOutlined className="text-gray-400" />
                </div>
                <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={value}
                    onChange={onChange}
                    autoComplete={autoComplete}
                    aria-invalid={!!error}
                    aria-describedby={error ? "password-error" : undefined}
                    className={inputClass(!!error, true)}
                    placeholder={placeholder}
                />
                <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 px-3.5 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                    {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </button>
            </div>
            <FieldError id="password-error" message={error} />
        </div>
    );
};

const InputField = ({ icon: Icon, label, hint, type = "text", name, value, onChange, placeholder, error, required = false, autoComplete, autoFocus }) => (
    <div>
        <label htmlFor={name} className="flex items-baseline justify-between text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            <span>{label} {required && <span className="text-red-500">*</span>}</span>
            {hint && <span className="text-xs font-normal text-gray-400">{hint}</span>}
        </label>
        <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <Icon className="text-gray-400" />
            </div>
            <input
                id={name}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                aria-invalid={!!error}
                aria-describedby={error ? `${name}-error` : undefined}
                className={inputClass(!!error, false)}
                placeholder={placeholder}
            />
        </div>
        <FieldError id={`${name}-error`} message={error} />
    </div>
);

const FEATURES = [
    { icon: ThunderboltOutlined, title: "Leads from every channel", text: "Meta, website and WhatsApp leads land and get assigned automatically." },
    { icon: BellOutlined, title: "Follow-ups that don't slip", text: "Reminders, demos and stage moves tracked for every rep." },
    { icon: LineChartOutlined, title: "Pipeline you can trust", text: "Live reports from first touch to won deal." },
    { icon: TeamOutlined, title: "Built for the whole team", text: "Roles, targets and handovers from sales to onboarding." },
];

// Decorative only: a stylised pipeline card, not real data.
const PIPELINE_PREVIEW = [
    { stage: "New", width: "88%", tone: "bg-brand-300" },
    { stage: "Follow-up", width: "64%", tone: "bg-brand-400" },
    { stage: "Demo", width: "41%", tone: "bg-brand-500" },
    { stage: "Won", width: "23%", tone: "bg-emerald-400" },
];

const BrandPanel = () => (
    <div className="hidden lg:flex relative flex-col justify-between overflow-hidden bg-brand-950 text-white p-12 xl:p-16">
        {/* Glow + grid backdrop */}
        <div className="pointer-events-none absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-brand-600/40 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-48 -right-24 w-[480px] h-[480px] rounded-full bg-fuchsia-500/25 blur-[120px]" />
        <div
            className="pointer-events-none absolute inset-0 opacity-[0.07]"
            style={{
                backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
                backgroundSize: "44px 44px",
                maskImage: "radial-gradient(ellipse at 30% 30%, black 20%, transparent 75%)",
                WebkitMaskImage: "radial-gradient(ellipse at 30% 30%, black 20%, transparent 75%)",
            }}
        />

        <div className="relative z-10 flex items-center gap-3">
            <LogoMark />
            <span className="text-lg font-semibold tracking-tight">Gro Wing CRM</span>
        </div>

        <div className="relative z-10 max-w-lg">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-brand-100">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                Sales workspace
            </span>
            <h1 className="mt-5 text-4xl xl:text-5xl font-semibold leading-[1.1] tracking-tight">
                Every lead, every follow-up,{" "}
                <span className="bg-gradient-to-r from-brand-200 via-brand-300 to-fuchsia-300 bg-clip-text text-transparent">
                    one place.
                </span>
            </h1>
            <p className="mt-4 text-brand-100/80 text-base leading-relaxed">
                Capture, assign and close deals faster, with your whole team working from the same pipeline.
            </p>

            {/* Product preview */}
            <div aria-hidden="true" className="mt-10 rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-md p-5 shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-white/90">Pipeline this month</span>
                    <span className="text-xs text-emerald-300 flex items-center gap-1">
                        <CheckCircleFilled /> Live
                    </span>
                </div>
                <div className="space-y-3">
                    {PIPELINE_PREVIEW.map((p) => (
                        <div key={p.stage} className="flex items-center gap-3">
                            <span className="w-20 shrink-0 text-xs text-brand-100/70">{p.stage}</span>
                            <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                                <div className={`h-full rounded-full ${p.tone}`} style={{ width: p.width }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-6">
                {FEATURES.map(({ icon: Icon, title, text }) => (
                    <div key={title} className="flex gap-3">
                        <div className="w-9 h-9 shrink-0 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center text-brand-200">
                            <Icon />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-white">{title}</p>
                            <p className="mt-0.5 text-xs leading-relaxed text-brand-100/70">{text}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>

        <p className="relative z-10 text-xs text-brand-100/50">
            © {new Date().getFullYear()} Tecnospice. All rights reserved.
        </p>
    </div>
);

/** The in-app page to return to after login (ProtectedRoute's ?next=), or null.
 *  Only a local path: "//evil.com" or "https://..." must never be followed. */
export function safeNext(next) {
    if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return null;
    return next === "/" ? null : next;
}

// ------------------- MAIN LOGIN -------------------
const Login = () => {
    const navigate = useNavigate();
    const { loginauth, isAuthenticated } = useContext(AuthContext);
    const { create } = useApiMethods();
    const [searchParams] = useSearchParams();
    const next = safeNext(searchParams.get("next"));

    const [loading, setLoading] = useState(false);
    const [keepSignedIn, setKeepSignedIn] = useState(false);
    const [values, setValues] = useState({
        org_slug: "",
        username: "",
        password: "",
        firstname: "",
        lastname: "",
        email: "",
        phonenumber: ""
    });
    const [mode, setMode] = useState("login");
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isAuthenticated && sessionStorage.getItem("token")) {
            const role = sessionStorage.getItem("login_role");
            navigate(role === "third_party" ? "/partner-portal/dashboard" : (next || getFirstAccessibleRoute()));
        }
    }, [isAuthenticated, navigate, next]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!values.username.trim()) {
            newErrors.username = "Username is required";
        }

        if (!values.password) {
            newErrors.password = "Password is required";
        } else if (values.password.length < 6) {
            newErrors.password = "Password must be at least 6 characters";
        }

        if (mode === "register") {
            if (!values.firstname.trim()) {
                newErrors.firstname = "First name is required";
            }
            if (!values.email.trim()) {
                newErrors.email = "Email is required";
            } else if (!/\S+@\S+\.\S+/.test(values.email)) {
                newErrors.email = "Email is invalid";
            }
            if (!values.phonenumber.trim()) {
                newErrors.phonenumber = "Phone number is required";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLogin = async (payload) => {
        try {
            const response = await create(LOGIN_URL, payload, false, setLoading);

            if (response?.status === "success") {
                loginauth(response.data, keepSignedIn);
                const dest = response.data?.login_role === "third_party"
                    ? "/partner-portal/dashboard"
                    : (next || getFirstAccessibleRoute());
                navigate(dest, { replace: true });
                ToastMsg("success", "Welcome back! Successfully signed in.");
            } else if (response?.status === "fail") {
                ToastMsg("error", response.message);
            }
        } catch (error) {
            ToastMsg("error", error.message || "Something went wrong");
        }
    };

    const handleRegister = async (payload) => {
        try {
            const response = await create(CREATE_LOGIN_USER_URL, payload, false, setLoading);

            if (response?.status === "success") {
                loginauth(response.data, keepSignedIn);
                ToastMsg("success", "Account created successfully! Welcome!");
                setMode("login")
            } else if (response?.status === "fail") {
                ToastMsg("error", response.message);
            }
        } catch (error) {
            ToastMsg("error", error.message || "Something went wrong");
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            ToastMsg("error", "Please fix the errors in the form");
            return;
        }

        const payload = mode === "login"
            ? {
                user_name: values.username,
                password: values.password,
                org_slug: values.org_slug,
            }
            : {
                user_name: values.username,
                password: values.password,
                login_fname: values.firstname,
                login_lname: values.lastname,
                login_email_address: values.email,
                login_phone_number: values.phonenumber
            };

        if (mode === "login") {
            handleLogin(payload);
        } else {
            handleRegister(payload);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] font-sans bg-white dark:bg-gray-950">
            <BrandPanel />

            {/* Form side */}
            <div className="relative flex flex-col min-h-screen">
                {/* Soft tint behind the form on small screens, where the brand panel is hidden */}
                <div className="lg:hidden pointer-events-none absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-brand-50 to-transparent dark:from-brand-950/40" />

                <div className="relative flex-1 flex items-center justify-center px-6 py-12 sm:px-10">
                    <div className="w-full max-w-[400px]">
                        {/* Mobile logo */}
                        <div className="lg:hidden flex items-center gap-3 mb-10">
                            <LogoMark />
                            <span className="text-lg font-semibold tracking-tight text-gray-900 dark:text-white">Gro Wing CRM</span>
                        </div>

                        <div className="mb-8">
                            <h2 className="text-[28px] leading-tight font-semibold tracking-tight text-gray-900 dark:text-white">
                                {mode === "login" ? "Sign in to your workspace" : "Create your account"}
                            </h2>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                {mode === "login"
                                    ? "Welcome back. Enter your details to continue."
                                    : "Join your team and start managing leads."}
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} noValidate className="space-y-5">
                            {mode === "login" && (
                                <InputField
                                    icon={BankOutlined}
                                    label="Organization ID"
                                    hint="Optional"
                                    name="org_slug"
                                    value={values.org_slug}
                                    onChange={handleChange}
                                    placeholder="e.g. apollo-hospitals"
                                    autoComplete="organization"
                                />
                            )}

                            <InputField
                                icon={UserOutlined}
                                label="Username"
                                name="username"
                                value={values.username}
                                onChange={handleChange}
                                placeholder="Enter your username"
                                error={errors.username}
                                autoComplete="username"
                                autoFocus
                                required
                            />

                            {mode === "register" && (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <InputField
                                            icon={UserOutlined}
                                            label="First Name"
                                            name="firstname"
                                            value={values.firstname}
                                            onChange={handleChange}
                                            placeholder="First name"
                                            error={errors.firstname}
                                            autoComplete="given-name"
                                            required
                                        />
                                        <InputField
                                            icon={UserOutlined}
                                            label="Last Name"
                                            name="lastname"
                                            value={values.lastname}
                                            onChange={handleChange}
                                            placeholder="Last name"
                                            autoComplete="family-name"
                                        />
                                    </div>

                                    <InputField
                                        icon={MailOutlined}
                                        label="Email"
                                        type="email"
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        placeholder="Enter your email"
                                        error={errors.email}
                                        autoComplete="email"
                                        required
                                    />

                                    <InputField
                                        icon={PhoneOutlined}
                                        label="Phone Number"
                                        type="tel"
                                        name="phonenumber"
                                        value={values.phonenumber}
                                        onChange={handleChange}
                                        placeholder="Enter your phone number"
                                        error={errors.phonenumber}
                                        autoComplete="tel"
                                        required
                                    />
                                </>
                            )}

                            <PasswordInput
                                value={values.password}
                                onChange={handleChange}
                                error={errors.password}
                                autoComplete={mode === "login" ? "current-password" : "new-password"}
                                placeholder={mode === "login" ? "Enter your password" : "Create a password"}
                            />

                            {mode === "login" && (
                                <div className="flex items-center justify-between">
                                    <label className="flex items-center cursor-pointer select-none">
                                        <Checkbox
                                            checked={keepSignedIn}
                                            onChange={(e) => setKeepSignedIn(e.target.checked)}
                                        />
                                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                                            Keep me signed in
                                        </span>
                                    </label>
                                    <button
                                        type="button"
                                        className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className={`group w-full h-11 px-4 rounded-lg font-medium text-sm text-white flex items-center justify-center gap-2 transition-all duration-150 ${loading
                                    ? "bg-brand-400 cursor-not-allowed"
                                    : "bg-brand-600 hover:bg-brand-700 active:scale-[0.99] shadow-[0_10px_24px_-12px_var(--brand-glow)] focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-200 dark:focus-visible:ring-brand-800"
                                    }`}
                            >
                                {loading ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                        {mode === "login" ? "Signing in..." : "Creating account..."}
                                    </>
                                ) : (
                                    <>
                                        {mode === "login" ? "Sign in" : "Create account"}
                                        <ArrowRightOutlined className="text-xs transition-transform duration-150 group-hover:translate-x-0.5" />
                                    </>
                                )}
                            </button>
                        </form>

                        <p className="mt-8 text-center text-xs text-gray-400 dark:text-gray-500">
                            Trouble signing in? Contact your workspace admin.
                        </p>
                    </div>
                </div>

                <p className="lg:hidden relative pb-6 text-center text-xs text-gray-400">
                    © {new Date().getFullYear()} Tecnospice
                </p>
            </div>
        </div>
    );
};

export default Login;
